/**
 * @module Utils
 *
 * Web signup funnel events (CN-2742). Every event goes through send(), which
 * adds the shared properties and returns the promise from
 * AnalyticsService.trackNow(), so a step that redirects right after can wait
 * for its events. The wrappers map the app's answers to the spec's values.
 */
import { AnalyticsService } from "app/services";
import { captureUTMParams, getEntryParams } from "app/utils/captureUTMParams";
import { getClickIdParams } from "app/utils/getClickIdParams";
import { getUTMParams } from "app/utils/hooks/getUTMParams";

/** Bump whenever the signup flow changes, so reports can split old from new. */
const FLOW_VERSION = "1";

const FORM_STARTED_KEY = "signup_form_started";

// Stands in for the sessionStorage flag when storage is blocked.
let formStartedWithoutStorage = false;

export type FunnelPlan = "pro" | "academy" | "analyze" | "invited_join";

// Inside an iframe the app is embedded in another site.
const surface = (): "web_app" | "web_embed" =>
  window.top !== window.self ? "web_embed" : "web_app";

const sharedProps = () => {
  const utm = getUTMParams();
  return {
    Surface: surface(),
    ...getEntryParams(),
    utm_source: utm.utm_source,
    utm_medium: utm.utm_medium,
    utm_campaign: utm.utm_campaign,
    gclid: getClickIdParams().gclid || undefined,
    "Flow Version": FLOW_VERSION,
  };
};

const send = (name: string, props: { [key: string]: any } = {}): Promise<void> =>
  AnalyticsService.trackNow(name, { ...sharedProps(), ...props });

/**
 * The form's mount effect can run before App's capture effect (a child's
 * effects run before its parent's), so the first event reads the landing
 * URL's attribution itself.
 */
export const trackSignupStarted = () => {
  captureUTMParams();
  return send("Signup Started");
};

/** Once per browser tab: later focuses, or a second visit to the form, send nothing. */
export const trackSignupFormStarted = (firstField: string): Promise<void> => {
  try {
    if (sessionStorage.getItem(FORM_STARTED_KEY)) {
      return Promise.resolve();
    }
    sessionStorage.setItem(FORM_STARTED_KEY, "1");
  } catch {
    // storage blocked: once per page load instead
    if (formStartedWithoutStorage) {
      return Promise.resolve();
    }
    formStartedWithoutStorage = true;
  }
  return send("Signup Form Started", { "First Field": firstField });
};

export const trackSignupFieldError = (field: string, errorType: string) =>
  send("Signup Field Error", { Field: field, "Error Type": errorType });

// How an account was created on the web: by phone when the visitor confirmed
// their number by SMS first (phone invites, where email is optional), else by
// email. Those are the only two web signups; social sign-ins aren't tracked.
export type SignupMethod = "email" | "phone";

export const trackSignupCompleted = (signedUpUsing: SignupMethod) =>
  send("Signup Completed", { "Signed Up Using": signedUpUsing });

export const trackRoleQuestionViewed = () => send("Role Question Viewed");

/** The app stores Parent / Fan as "other"; the spec calls it parent_fan. */
export const trackRoleSelected = (
  kind: string,
  sport: string | null,
  title: string | null,
) =>
  send("Role Selected", {
    Kind: kind === "other" ? "parent_fan" : kind,
    Sport: sport,
    Title: title,
  });

export const trackCoachQuestionViewed = () => send("Coach Question Viewed");

export const trackCoachTypeSelected = (target: "pro" | "academy") =>
  send("Coach Type Selected", {
    "Coach Type": target === "academy" ? "facility" : "independent",
  });

export const trackInviteQuestionViewed = () => send("Invite Question Viewed");

export const trackInviteAnswered = (answer: "yes" | "no") =>
  send("Invite Answered", { "Was Invited": answer === "yes" });

export const trackPlanMatched = (plan: FunnelPlan) =>
  send("Plan Matched", { "Plan Type": plan });

const CHECKOUT_PLANS: { [plan: string]: FunnelPlan } = {
  pro: "pro",
  coachpro: "pro",
  academy: "academy",
  analyze: "analyze",
};

/**
 * The plan a checkout link (/trial/<plan>, /purchase/<plan>) sells, or null
 * for any other return URL, e.g. /pga/start-trial or a page behind sign-in.
 */
export const planFromCheckoutUrl = (url: string): FunnelPlan | null => {
  const [path] = url.split("?");
  const plan = path.match(/\/(?:trial|purchase)\/([^/#]+)/)?.[1];
  return (plan && CHECKOUT_PLANS[plan]) || null;
};
